import BigNumber from "bignumber.js"
import { ethers } from "ethers"
import { formatUnits } from "ethers/lib/utils.js"

import { labelNumber } from "common/helpers/number"
import type { TokenBalances } from "common/types/tokenBalances"
import TransferArbitrumErc20Action from "createProposal/action/TransferArbitrumErc20Action"
import TransferErc20Action from "createProposal/action/TransferErc20Action"
import TransferNativeAssetAction from "createProposal/action/TransferNativeAssetAction"
import type { Action } from "createProposal/action/types"
import type { ExecutableCall } from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import { isSameAddress } from "web3/helpers/address"
import SwapAction from "createProposal/action/SwapAction"
import CustomAction from "createProposal/action/CustomAction"

type FeeInfo = {
  symbol: string
  tallyFeeUSD: string
  tallyFeeToken: string
}

const FEE_MULTIPLIER = 10000

export const getTotalFeesFromActions = (
  actions: Action[],
  tokenBalances?: TokenBalances,
): FeeInfo[] => {
  return actions.reduce<FeeInfo[]>((acc, action) => {
    let selectedAsset
    let tallyFeeToken
    let tallyFeeUSDValue

    if (
      action instanceof TransferNativeAssetAction ||
      action instanceof TransferErc20Action
    ) {
      selectedAsset =
        tokenBalances?.tokens?.find((token) =>
          isSameAddress(
            token.tokenInfo.address,
            action.targetAddress as string,
          ),
        ) || tokenBalances?.nativeAsset

      if (!selectedAsset) return acc

      const tallyFeeAmount = new BigNumber(action.value)
        .multipliedBy(action.tallyFee || 0)
        .toNumber()

      if (tallyFeeAmount === 0) return acc

      tallyFeeToken = tallyFeeAmount.toString()
      tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    } else if (action instanceof SwapAction) {
      selectedAsset =
        tokenBalances?.tokens?.find((token) =>
          isSameAddress(
            token.tokenInfo.address,
            action.sellToken?.address as string,
          ),
        ) ||
        (action.sellToken?.symbol === "ETH" && tokenBalances?.nativeAsset)

      if (!selectedAsset) return acc

      const bigNumberFee = new BigNumber(action.fee).toFixed() // Converts to full decimal string
      const feeInUnits = formatUnits(
        bigNumberFee,
        selectedAsset.tokenInfo.decimals,
      )

      const tallyFeeAmount = new BigNumber(feeInUnits)

      if (tallyFeeAmount.isZero()) return acc

      tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? tallyFeeAmount
            .multipliedBy(selectedAsset.tokenInfo.price.rate)
            .toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken: tallyFeeAmount.toString(),
      })
    }

    return acc
  }, [])
}

const transferAbiFragment = [
  "function transfer(address to, uint256 value) returns (bool)",
]

// Function to decode ERC20 transfer calldata
const decodeErc20TransferCalldata = (calldata: string) => {
  // Create an Interface object for ERC20
  const iface = new ethers.utils.Interface(transferAbiFragment)

  try {
    // Decode the calldata
    const decoded = iface.parseTransaction({ data: calldata })

    // Assuming it's a transfer function, extract the 'to' address and 'value'
    const toAddress = decoded.args[0]
    const value = decoded.args[1]

    return {
      toAddress,
      value: value.toString(), // Convert BigNumber to string for easier handling
    }
  } catch (error) {
    console.error("Error decoding calldata:", error)

    return null // Or handle the error as appropriate for your application
  }
}

export const getTotalFeesFromExecutableCalls = (
  executables: ExecutableCall[],
  tokenBalances?: TokenBalances,
): FeeInfo[] => {
  return executables.reduce<FeeInfo[]>((acc, executable) => {
    // Determine if the executable is related to a transfer native action based on the type
    if (executable.type === ExecutableCallType.Nativetransfer) {
      const selectedAsset = tokenBalances?.nativeAsset

      if (!selectedAsset) return acc // Skip if no related token found

      // Calculate fees
      const tallyFeeToken =
        executable.offchaindata && "tallyFee" in executable.offchaindata
          ? new BigNumber(
              formatUnits(
                executable.value as string,
                selectedAsset.tokenInfo.decimals,
              ),
            )
              .multipliedBy(executable.offchaindata.tallyFee / FEE_MULTIPLIER)
              .toString()
          : "0"

      const tallyFeeAmount = new BigNumber(tallyFeeToken).toNumber()
      const tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    }

    if (executable.type === ExecutableCallType.Erc20transfer) {
      const selectedAsset = tokenBalances?.tokens?.find((token) =>
        isSameAddress(token.tokenInfo.address, executable.target),
      )

      if (!selectedAsset) return acc // Skip if no related token found

      const decodedCalldata = decodeErc20TransferCalldata(
        executable.calldata as string,
      )

      const value = decodedCalldata?.value

      // Calculate fees
      const tallyFeeToken =
        executable.offchaindata && "tallyFee" in executable.offchaindata
          ? new BigNumber(
              formatUnits(
                value ? value : 0,
                Number(selectedAsset.tokenInfo.decimals),
              ),
            )
              .multipliedBy(executable.offchaindata.tallyFee / FEE_MULTIPLIER)
              .toString()
          : "0"

      const tallyFeeAmount = new BigNumber(tallyFeeToken).toNumber()
      const tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    }

    if (executable.type === ExecutableCallType.Erc20transferarbitrum) {
      const selectedAsset = tokenBalances?.tokens?.find((token) =>
        isSameAddress(token.tokenInfo.address, executable.target),
      )

      if (!selectedAsset) return acc // Skip if no related token found

      const decodedCalldata = decodeErc20TransferCalldata(
        executable.calldata as string,
      )

      const value = decodedCalldata?.value

      // Calculate fees
      const tallyFeeToken =
        executable.offchaindata && "tallyFee" in executable.offchaindata
          ? new BigNumber(formatUnits(value, selectedAsset.tokenInfo.decimals))
              .multipliedBy(executable.offchaindata.tallyFee / FEE_MULTIPLIER)
              .toString()
          : "0"

      const tallyFeeAmount = new BigNumber(tallyFeeToken).toNumber()
      const tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    }

    if (
      executable.type === ExecutableCallType.Swap &&
      executable.signature === "transfer(address,uint256)"
    ) {
      const selectedAsset = tokenBalances?.tokens?.find((token) =>
        isSameAddress(token.tokenInfo.address, executable.target),
      )

      if (!selectedAsset) return acc // Skip if no related token found

      const decodedCalldata = decodeErc20TransferCalldata(
        executable.calldata as string,
      )

      const value = decodedCalldata?.value

      // Calculate fees
      const tallyFeeToken = new BigNumber(
        formatUnits(value, selectedAsset.tokenInfo.decimals),
      ).toString()

      const tallyFeeAmount = new BigNumber(tallyFeeToken).toNumber()
      const tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    }

    if (executable.type === ExecutableCallType.Swap && !executable.signature) {
      const selectedAsset = tokenBalances?.nativeAsset

      if (!selectedAsset) return acc // Skip if no related token found

      // Calculate fees
      const tallyFeeToken = new BigNumber(
        formatUnits(
          executable.value as string,
          selectedAsset.tokenInfo.decimals,
        ),
      ).toString()

      const tallyFeeAmount = new BigNumber(tallyFeeToken).toNumber()
      const tallyFeeUSDValue = selectedAsset.tokenInfo.price.rate
        ? (tallyFeeAmount * selectedAsset.tokenInfo.price.rate).toString()
        : "N/A"

      acc.push({
        symbol: selectedAsset.tokenInfo.symbol,
        tallyFeeUSD: tallyFeeUSDValue,
        tallyFeeToken,
      })
    }

    return acc
  }, [])
}

type FeesAccumulator = {
  [symbol: string]: {
    totalUSD: number
    tallyFeeToken: number
  }
}

export const formatFeesForDisplay = (fees: FeeInfo[]): string => {
  const feesBySymbol = fees.reduce<FeesAccumulator>(
    (acc, { symbol, tallyFeeUSD, tallyFeeToken }) => {
      const usdValue =
        parseFloat(tallyFeeUSD.replace(/[^\d.-]/g, "")) *
        (tallyFeeUSD.includes("k") ? 1000 : 1)
      const tokenValue =
        parseFloat(tallyFeeToken.replace(/[^\d.-]/g, "")) *
        (tallyFeeToken.includes("k") ? 1000 : 1)

      if (!acc[symbol]) {
        acc[symbol] = { totalUSD: 0, tallyFeeToken: 0 }
      }

      acc[symbol].totalUSD += usdValue
      acc[symbol].tallyFeeToken += tokenValue

      return acc
    },
    {},
  )

  // Check if there are no fees
  const totalUSD = Object.values(feesBySymbol).reduce(
    (sum, { totalUSD }) => sum + totalUSD,
    0,
  )

  const totalTokens = Object.values(feesBySymbol).reduce(
    (sum, { tallyFeeToken }) => sum + tallyFeeToken,
    0,
  )

  if (totalUSD === 0 && totalTokens === 0) {
    return "" // Return an empty string if all fee values are zero
  }

  // Construct fee string if there are fees
  const feesParts = Object.entries(feesBySymbol)
    .filter(([_, { tallyFeeToken }]) => tallyFeeToken !== 0) // Filter out tokens with zero amount
    .map(([symbol, { tallyFeeToken }]) => {
      return `${labelNumber(tallyFeeToken)} ${symbol}`
    })

  const formattedTotalUSD = labelNumber(totalUSD)

  return `Tally Fees: ${feesParts.join(" + ")} (Total: $${formattedTotalUSD})`
}

export const shouldRenderReceiptComponent = (actions: Action[]): boolean => {
  const allIncomplete = actions.every((action) => {
    if (
      action instanceof TransferNativeAssetAction ||
      action instanceof TransferErc20Action ||
      action instanceof TransferArbitrumErc20Action
    ) {
      const getRecipientAddress = () => {
        if (
          action instanceof TransferErc20Action ||
          action instanceof TransferArbitrumErc20Action
        ) {
          return Boolean(action.recipient?.trim())
            ? (action.recipient?.trim() as string)
            : "0x"
        }

        const target = !!action.targetAddress ? action.targetAddress : "0x"

        return target
      }

      const recipientAddress = getRecipientAddress()

      return recipientAddress === "Ox" || recipientAddress === ""
    } else if (action instanceof CustomAction) {
      // Check if custom action has targetAddress and selectedAbiMethod
      return !action.targetAddress || !action.selectedAbiMethod
    } else if (action instanceof SwapAction) {
      // Check if swap action has buyToken, quote, and sellToken

      return !action.buyToken || !action.quote || !action.sellToken
    }

    return false // Assume actions not explicitly checked are complete
  })

  return !allIncomplete
}
